<template>
  <div>
    <b-card>
      <b-row class="mb-md-2 mb-0">
        <b-col
            md="4"
            class="mb-md-0 mb-2"
        >
          <div>
            <h5 class="font-weight-bold">
              {{ $t('Manufacturer') }}
            </h5>
            <v-select
                @input="handleFilterChange"
                v-model="assetBrandId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="manufacturerOptions"
                :reduce="val => val.value"
                :clearable="false"
                input-id="manufacturer"
            />
          </div>
        </b-col>
        <b-col
            md="4"
            class="mb-md-0 mb-2"
        >
          <div>
            <h5 class="font-weight-bold">
              {{ $t('Model') }}
            </h5>
            <v-select
                @input="handleFilterChange"
                v-model="assetModelId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="modelOptions"
                :reduce="val => val.value"
                :clearable="false"
                input-id="model"
            />
          </div>
        </b-col>
        <b-col
            md="4"
            class="mb-md-0 mb-2"
        >
          <div>
            <h5 class="font-weight-bold">
              {{ $t('Asset Type') }}
            </h5>
            <v-select
                @input="handleFilterChange"
                v-model="assetTypeId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="assetsOptions"
                :reduce="val => val.value"
                :clearable="false"
                input-id="asset-type"
            />
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col
            cols="12"
            md="4"
            class="mb-md-0 mb-2"
        >
          <h5 class="font-weight-bold">
            {{ $t('Choose the Date') }}
          </h5>
          <div class="d-flex">
            <flat-pickr
                id="date-range-picker"
                v-model="dateRange"
                :config="flatpickrConfig"
                class="form-control flat-picker"
            />
            <b-button
                variant="primary"
                class="border-left-0"
                style="border-radius: 0 0.357rem 0.357rem 0;"
                :disabled="isLoading"
                @click="handleFilterChange"
            >
              <feather-icon
                  icon="ArrowRightIcon"
                  size="16"
              />
            </b-button>
          </div>
        </b-col>
        <b-col
            v-if="!isCustomer"
            cols="12"
            md="4"
        >
          <h5 class="font-weight-bold">
            {{ $t('Customer') }}
          </h5>
          <div class="d-flex">
            <vue-autosuggest
                v-model="searchCustomer"
                :suggestions="filteredOptionsCustomer"
                :input-props="inputPropsCustomer"
                class="flex-fill"
                @selected="selectCustomerHandler"
                @input="onInputCustomerChange"
            >
              <template slot-scope="{suggestion}">
                <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
              </template>
            </vue-autosuggest>
            <b-button
                :disabled="searchCustomer.length === 0"
                size="sm"
                style="border-radius: 0 0.357rem 0.357rem 0;"
                @click="clearCustomerInput"
            >
              <feather-icon
                  icon="XIcon"
                  size="16"
              />
            </b-button>
          </div>
        </b-col>
        <b-col
            cols="12"
            md="4"
        >
          <h5 class="font-weight-bold">
            {{ $t('Search') }}
          </h5>
          <div class="d-flex">
            <b-form-input
                v-model="search"
                :placeholder="$t('Search...')"
                class="search-input"
                @keyup.enter="handleFilterChange"
            />
            <b-button
                variant="primary"
                class="border-left-0"
                style="border-radius: 0 0.357rem 0.357rem 0;"
                :disabled="isLoading"
                @click="handleFilterChange"
            >
              <feather-icon
                  icon="SearchIcon"
                  size="16"
              />
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <b-card
        class="mt-2"
        no-body
    >
      <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
        <b-card-title>{{ $t('Physical Availability (DF)') }}</b-card-title>

        <div class="d-flex align-items-center font-small-2 mt-sm-0 mt-1">
          <div class="d-flex align-items-center mr-2">
            <span class="dot" style="background-color: #28C76F;"></span>
            <span>{{ $t('Ideal threshold') }}</span>
          </div>
          <div class="d-flex align-items-center">
            <span class="dot" style="background-color: #EA5455;"></span>
            <span>{{ $t('Attention threshold') }}</span>
          </div>
        </div>
      </b-card-header>

      <div
          v-if="isLoading"
          class="d-flex justify-content-center align-items-center p-5"
      >
        <b-spinner
            style="width: 3rem; height: 3rem;"
            :label="$t('Loading')"
        />
      </div>

      <div
          v-if="!isLoading && dashboardAssets.length === 0"
          class="d-flex justify-content-center align-items-center p-5 text-center"
      >
        <div>
          <feather-icon
              icon="BarChart2Icon"
              size="48"
              class="text-muted"
          />
          <h4 class="mt-2 text-muted">
            {{ $t('No data found for the selected period.') }}
          </h4>
          <p class="text-muted">
            {{ $t('Try changing the filters to see results.') }}
          </p>
        </div>
      </div>

      <div
          v-if="!isLoading && dashboardAssets.length > 0"
          class="asset-dashboard-container"
      >
        <div class="asset-row-header d-none d-lg-flex">
          <div class="col-lg-2">
            <h6 class="text-uppercase text-muted">
              {{ $t('Identifier') }}
            </h6>
          </div>
          <div class="col-lg-1 text-center">
            <h6 class="text-uppercase text-muted">
              {{ $t('Accumulated DF') }}
            </h6>
          </div>
          <div class="col-lg-9">
            <h6 class="text-uppercase text-muted">
              {{ $t('Daily DF') }}
            </h6>
          </div>
        </div>

        <div
            v-for="asset in dashboardAssets"
            :key="asset.assetId"
            class="asset-card"
        >
          <div class="asset-info col-lg-2">
            <b-avatar
                size="60px"
                :src="asset.iconUrl ? asset.iconUrl : null"
                variant="light-info"
                class="asset-avatar"
            >
              <feather-icon
                  v-if="!asset.iconUrl"
                  icon="TruckIcon"
                  size="28"
              />
            </b-avatar>
            <div class="asset-details">
              <h5 class="font-weight-bolder mb-0">
                {{ asset.descriptor }}
              </h5>
            </div>
          </div>

          <div class="df-accumulated col-lg-1 text-center">
            <vue-apex-charts
                type="radialBar"
                height="120"
                :options="asset.gaugeOptions"
                :series="asset.gaugeSeries"
            />
          </div>

          <div class="df-daily col-lg-9">
            <vue-apex-charts
                type="line"
                height="180"
                :options="asset.chartOptions"
                :series="asset.chartSeries"
            />
          </div>
        </div>
      </div>

      <b-card-footer v-if="!isLoading && totalItems > perPage">
        <div class="d-flex justify-content-between align-items-center">
          <span class="text-muted">
            Exibindo {{ dashboardAssets.length }} de {{ totalItems }} itens
          </span>

          <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              class="mb-0"
              @change="handlePageChange"
          >
            <template #prev-text>
              <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
              />
            </template>
          </b-pagination>
        </div>
      </b-card-footer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BAvatar, BSpinner, BCardHeader, BCardTitle, BCardFooter, BPagination, BFormInput
} from 'bootstrap-vue'
import {VueAutosuggest} from 'vue-autosuggest'
import VueApexCharts from 'vue-apexcharts'
import flatPickr from 'vue-flatpickr-component'
import store from '@/store'
import {onUnmounted, ref} from '@vue/composition-api'
import i18n from '@/libs/i18n'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'

import managementStoreModule from '../managementStoreModule'
import customerStoreModule from '../../customer/customerStoreModule'
import assetStoreModule from "@/views/main/asset/assetStoreModule";

export default {
  components: {
    BCard, BRow, BCol, BButton, BAvatar, BSpinner, BCardHeader, BCardTitle, BCardFooter, BPagination, BFormInput,
    VueAutosuggest, vSelect, VueApexCharts, flatPickr,
  },
  data() {
    return {
      search:'',
      dateRange: '',
      searchCustomer: '',
      customerId: null,
      assetTypeId: null,
      assetModelId: null,
      assetBrandId: null,
      isLoading: false,
      dashboardAssets: [],
      filteredOptionsCustomer: [],
      inputPropsCustomer: {
        id: 'autosuggest__input_customer',
        class: 'form-control',
        placeholder: this.$t('Search customer...'),
      },
      flatpickrConfig: {
        mode: 'range',
        dateFormat: 'd/m/Y',
        maxDate: new Date(),
      },
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
    }
  },
  computed: {
    isCustomer() {
      const {userData} = store.state.app
      return userData && userData.isCustomer
    },
  },
  mounted() {
    this.initializeDates()
    this.fetchMaintenanceReport()
  },
  methods: {
    initializeDates() {
      const end = new Date()
      const start = new Date()
      start.setDate(end.getDate() - 9)
      this.dateRange = `${this.formatDate(start)} to ${this.formatDate(end)}`
    },

    async fetchMaintenanceReport() {
      if (this.isLoading) return
      const [start, end] = this.formatDateRange(this.dateRange, 'date')
      if (!start || !end) return

      this.isLoading = true

      try {
        const params = {
          search: this.search,
          start, end,
          customerId: this.customerId,
          assetTypeId: this.assetTypeId,
          assetBrandId: this.assetBrandId,
          assetModelId: this.assetModelId,
          page: this.currentPage,
          perPage: this.perPage,
        }

        const response = await store.dispatch('app-management/fetchMaintenanceTimeReport', params)

        this.totalItems = response.data.totalItems;
        this.processReportData(response.data.assets);

      } catch (error) {
        this.totalItems = 0;
        this.dashboardAssets = [];
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('Error fetching report'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },

    handlePageChange(newPage) {
      this.currentPage = newPage;
      this.fetchMaintenanceReport();
    },

    handleFilterChange() {
      this.currentPage = 1;
      this.fetchMaintenanceReport();
    },

    processReportData(assets) {
      if (!assets) {
        this.dashboardAssets = []
        return
      }

      this.dashboardAssets = assets.map(asset => {
        const accumulatedDF = 100 - (asset.periodMaintenanceTimePercent || 0)
        const sortedDays = asset.days.sort((a, b) => new Date(a.date) - new Date(b.date))
        const dailyData = sortedDays.map(day => {
          const dateObj = new Date(day.date)
          const dayOfMonth = String(dateObj.getDate()).padStart(2, '0')
          const month = String(dateObj.getMonth() + 1).padStart(2, '0')

          return {
            x: `${dayOfMonth}/${month}`,
            y: 100 - (day.dayMaintenanceTimePercent || 0),
          }
        })

        return {
          assetId: asset.assetId,
          descriptor: asset.assetDescriptor,
          iconUrl: asset.assetModelIconUrl,
          accumulatedDF,
          chartSeries: [{ name: this.$t('Daily DF'), data: dailyData }],
          chartOptions: this.getChartOptions(dailyData),
          gaugeSeries: [parseFloat(accumulatedDF.toFixed(1))],
          gaugeOptions: this.getGaugeOptions(accumulatedDF),
        }
      })
    },

    getGaugeOptions(value) {
      const color = value >= 80 ? '#28C76F' : '#EA5455';


      return {
        chart: {
          type: 'radialBar',
          sparkline: {
            enabled: true,
          },
        },
        plotOptions: {
          radialBar: {
            startAngle: -90,
            endAngle: 90,
            hollow: {
              margin: 15,
              size: '65%',
            },
            track: {
              background: '#e9ecef',
              strokeWidth: '100%',
            },
            dataLabels: {
              name: {
                show: false,
              },
              value: {
                offsetY: -2,
                fontSize: '1.2rem',
                fontWeight: '600',
                color: '#4E4E4E',
                formatter: val => `${val}%`,
              },
            },
          },
        },
        fill: {
          colors: [color],
        },
        stroke: {
          lineCap: 'round',
        },
        labels: [this.$t('Accumulated DF')],
      }
    },

   

    getChartOptions(data) {
      const pointColors = data.map(point => this.getDFColor(point.y, 'hex'));

      const discreteMarkers = data.map((point, index) => ({
        seriesIndex: 0,
        dataPointIndex: index,
        fillColor: pointColors[index],
        strokeColor: '#fff',
        size: 6,
        shape: 'circle'
      }));

      return {
        annotations: {
          yaxis: [{
            y: 80,
            borderColor: '#4f4f4f',
            strokeDashArray: 4,
            label: {
              borderColor: '#b8c2cc',
              style: {
                color: '#fff',
                background: '#b8c2cc',
                fontSize: '12px',
                fontWeight: 'bold',
                padding: { left: 5, right: 5, top: 2, bottom: 2 }
              },
              text: '80%',
              position: 'right',
              offsetX: -10,
              offsetY: 10,
            },
          }],
        },
        chart: {
          type: 'line',
          toolbar: { show: false },
          sparkline: { enabled: false },
          parentHeightOffset: 0,
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350
            }
          }
        },
        grid: {
          show: true,
          borderColor: '#f1f1f1',
          strokeDashArray: 0,
          padding: {
            left: 25,
            right: 15,
            top: 10,
            bottom: 10
          },
          xaxis: {
            lines: {
              show: false
            }
          },
          yaxis: {
            lines: {
              show: true
            }
          }
        },
        
        colors: ['#82868b'],
        dataLabels: {
          enabled: true,
          offsetY: -10,
          background: {
            enabled: true,
            foreColor: '#fff',
            borderRadius: 3,
            padding: 4,
            opacity: 0.9,
            borderWidth: 0,
            dropShadow: {
              enabled: true,
              blur: 3,
              opacity: 0.15
            }
          },
          formatter: function(val, opts) {
            const pointIndex = opts.dataPointIndex;
            const color = pointColors[pointIndex];
            return `${val.toFixed(1)}%`;
          },
          style: {
            fontSize: '12px',
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontWeight: 'bold',
            colors: undefined
          },
          dropShadow: {
            enabled: false
          }
        },
        markers: {
          size: 6,
          colors: pointColors,
          strokeColors: '#fff',
          strokeWidth: 2,
          strokeOpacity: 0.9,
          strokeDashArray: 0,
          fillOpacity: 1,
          discrete: discreteMarkers,
          shape: 'circle',
          radius: 2,
          offsetX: 0,
          offsetY: 0,
          hover: {
            size: 8,
            sizeOffset: 3
          }
        },

        stroke: {
          show: true,
          curve: 'smooth',
          lineCap: 'butt',
          colors: ['#333333'], // Cor da linha principal
          width: 3,
          dashArray: 0
        },
        fill: {
          type: 'solid',
          opacity: 0.8
        },
        xaxis: {
          categories: data.map(point => point.x),
          labels: {
            style: {
              colors: '#6E6B7B',
              fontSize: '12px',
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontWeight: 400
            },
            rotateAlways: false,
            rotate: 0
          },
          axisTicks: {
            show: true,
            borderType: 'solid',
            color: '#78909C',
            height: 6,
            offsetX: 0,
            offsetY: 0
          },
          axisBorder: {
            show: true,
            color: '#78909C',
            height: 1,
            width: '100%',
            offsetX: 0,
            offsetY: 0
          },
          crosshairs: {
            show: true,
            width: 1,
            position: 'back',
            opacity: 0.9,
            stroke: {
              color: '#b6b6b6',
              width: 1,
              dashArray: 3
            }
          }
        },
        yaxis: {
          show: false,
          min: 0,
          max: 100,
        },
        tooltip: {
          enabled: true,
          enabledOnSeries: undefined,
          shared: false,
          followCursor: false,
          intersect: false,
          inverseOrder: false,
          custom: function({ series, seriesIndex, dataPointIndex, w }) {
            const value = series[seriesIndex][dataPointIndex];
            const date = w.globals.labels[dataPointIndex];
            const color = pointColors[dataPointIndex];

            return `
          <div class="apexcharts-tooltip-custom" style="
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            padding: 8px 12px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          ">
            <div style="font-weight: 600; margin-bottom: 4px; color: #333;">
              Dia: ${date}
            </div>
            <div style="display: flex; align-items: center;">
              <span style="
                display: inline-block;
                width: 10px;
                height: 10px;
                background: ${color};
                border-radius: 50%;
                margin-right: 6px;
              "></span>
              <span style="font-weight: 700; color: ${color};">
                ${value.toFixed(1)}%
              </span>
            </div>
          </div>
        `;
          },
          fillSeriesColor: false,
          theme: 'light',
          style: {
            fontSize: '12px',
            fontFamily: 'Helvetica, Arial, sans-serif'
          },
          onDatasetHover: {
            highlightDataSeries: true
          },
          x: {
            show: false
          },
          y: {
            formatter: undefined,
            title: {
              formatter: () => ''
            }
          },
          marker: {
            show: false
          },
          fixed: {
            enabled: false,
            position: 'topRight',
            offsetX: 0,
            offsetY: 0
          }
        },
        legend: {
          show: false
        },
        states: {
          normal: {
            filter: {
              type: 'none',
              value: 0
            }
          },
          hover: {
            filter: {
              type: 'lighten',
              value: 0.15
            }
          },
          active: {
            allowMultipleDataPointsSelection: false,
            filter: {
              type: 'darken',
              value: 0.35
            }
          }
        }
      }
    },

    getDFColor(dfValue, format = 'text') {
      const colors = {
        success: {text: 'text-success', bg: 'bg-success', hex: '#28C76F'},
        warning: {text: 'text-warning', bg: 'bg-warning', hex: '#FF9F43'},
        danger: {text: 'text-danger', bg: 'bg-danger', hex: '#EA5455'},
      }

      if (dfValue >= 90) return colors.success[format]
      if (dfValue >= 80) return colors.warning[format]
      return colors.danger[format]
    },

    formatDate(date) {
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },

    formatDateRange(dateRange, type = 'string') {
      try {
        if (!dateRange) return []
        const stringValues = dateRange.split(' to ')
        if (type === 'string') return stringValues
        return stringValues.map(this.dateFromString)
      } catch {
        return []
      }
    },

    dateFromString(string) {
      try {
        if (!string || typeof string !== 'string') return null
        const [day, month, year] = string.split('/')
        return new Date(year, month - 1, day)
      } catch {
        return null
      }
    },

    selectCustomerHandler(option) {
      if (option && option.item) {
        this.customerId = option.item.id;
        this.searchCustomer = option.item.name;
        this.filteredOptionsCustomer = [];
        this.handleFilterChange();
      }
    },

    onInputCustomerChange(text) {
      if (text === '' || text === undefined || text.length < 3) {
        if (this.customerId) this.clearCustomerInput()
        return
      }
      store.dispatch('app-customer/fetchCustomers', {q: text, perPage: 5, page: 1})
          .then(response => {
            this.filteredOptionsCustomer = [{data: response.data.data}]
          })
    },

    clearCustomerInput() {
      this.searchCustomer = '';
      this.customerId = null;
      this.filteredOptionsCustomer = [];
      this.handleFilterChange();
    },
  },

  setup() {
    const MANAGEMENT_APP_STORE_MODULE_NAME = 'app-management'
    const CUSTOMER_APP_STORE_MODULE_NAME = 'app-customer'
    const ASSET_APP_STORE_MODULE_NAME = 'app-asset'

    const manufacturerOptions = ref([{
      label: i18n.t('All Manufacturers'),
      value: null
    }])
    const modelOptions = ref([{
      label: i18n.t('All Models'),
      value: null
    }])
    const assetsOptions = ref([{
      label: i18n.t('All Assets'),
      value: null
    }])

    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
      store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)
    }
    if (!store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME)) {
      store.registerModule(CUSTOMER_APP_STORE_MODULE_NAME, customerStoreModule)
    }
    if (!store.hasModule(ASSET_APP_STORE_MODULE_NAME)) {
      store.registerModule(ASSET_APP_STORE_MODULE_NAME, assetStoreModule)
    }

    onUnmounted(() => {
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
      if (store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME)) store.unregisterModule(CUSTOMER_APP_STORE_MODULE_NAME)
      if (store.hasModule(ASSET_APP_STORE_MODULE_NAME)) store.unregisterModule(ASSET_APP_STORE_MODULE_NAME)
    })

    store.dispatch('app-asset/fetchAllAssetsManufacture', {})
        .then(response => {
          response.data.data.forEach(v => {
            manufacturerOptions.value.push({
              label: v.name,
              value: v.id
            });
          });
        })
        .catch(() => {})

    store.dispatch('app-asset/fetchAssetsType')
        .then(response => {
          response.data.data.map(function (v, key) {
            assetsOptions.value.push({
              label: v.name,
              value: v.id
            },);
          });
        })
        .catch(() => {})

    store.dispatch('app-asset/fetchAllAssetsModel', {id: ''})
        .then(response => {
          response.data.data.forEach(v => {
            modelOptions.value.push({
              label: v.name,
              value: v.id
            });
          });
        })
        .catch(() => {})

    return {
      modelOptions,
      assetsOptions,
      manufacturerOptions
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-select.scss';

.flat-picker {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.auto-suggestions-box {
  border-radius: 0.357rem 0 0 0.357rem;
}

------------------------------------

.asset-dashboard-container {
  padding: 0.5rem;
}

.asset-card {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background-color:#f9f9f9;
  border-radius: 0.5rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid #EBEEF0;
  transition: box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out;

  &:hover {
    box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  &:last-child {
    margin-bottom: 0.5rem;
  }
}

.asset-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0;

  .asset-details h5 {
    font-size: 1.1rem;
  }
}

.df-accumulated {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.df-daily {
  padding: 0;
}

.asset-row-header {
  padding: 0.75rem 1.5rem;

  h6 {
    font-size: 0.8rem;
    font-weight: 600;
  }
}

.dot {
  height: 12px;
  width: 12px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
}

@media (max-width: 991.98px) {
  .asset-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;

    .asset-info, .df-accumulated, .df-daily {
      width: 100%;
      max-width: 100%;
      padding: 0;
      flex: 0 0 100%;
    }

    .df-accumulated {
      justify-content: flex-start;
    }
  }
}
</style>